import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import { profileMessages } from '../profile/profileMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import styles from '../Outreach.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface TagEditorProps {
  values: string[];
  onChange: (values: string[]) => void;
  addLabel: string;
  /** The first values that matter most get the brand chip (the first 3 genres, design b1). */
  highlightFirst?: number;
  max?: number;
  disabled?: boolean;
  /** Changes a typed value before it is added (upper case for a country). */
  normalise?: (value: string) => string | null;
  testId?: string;
}

/** Chips with a remove button and one "+ Add" field (design outreach b1 genres, moods, markets). */
const TagEditorComponent: React.FC<TagEditorProps> = ({
  values,
  onChange,
  addLabel,
  highlightFirst = 0,
  max = 20,
  disabled = false,
  normalise,
  testId,
}) => {
  const intl = useIntl();
  const [draft, setDraft] = useState('');

  const add = useCallback(() => {
    const raw = draft.trim();
    const value = normalise === undefined ? raw : normalise(raw);
    setDraft('');
    if (value === null || value === '' || values.includes(value) || values.length >= max) {
      return;
    }
    onChange([...values, value]);
  }, [draft, max, normalise, onChange, values]);

  const chips = useMemo(
    () =>
      values.map((value, index) => (
        <span key={value} className={clsx(styles.chip, index < highlightFirst && styles.chipVio)}>
          {value}
          {!disabled && (
            <button
              type="button"
              className="btn btn-link p-0 lh-1 text-reset"
              aria-label={intl.formatMessage(profileMessages.remove, { name: value })}
              onClick={() => onChange(values.filter((other) => other !== value))}
            >
              ×
            </button>
          )}
        </span>
      )),
    [disabled, highlightFirst, intl, onChange, values]
  );

  return (
    <div className={styles.row} data-testid={testId}>
      {chips}
      {!disabled && values.length < max && (
        <input
          className={`${styles.input} w-auto`}
          // The width follows the hint, so "Add a country (2 letters, for example DE)" is not cut.
          size={Math.max(12, addLabel.length)}
          value={draft}
          placeholder={addLabel}
          aria-label={addLabel}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={add}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              add();
            }
          }}
        />
      )}
    </div>
  );
};

export const TagEditor = TypedMemo(TagEditorComponent);
